import { z } from 'zod';
import { coreIds } from './ids.js';

export const SecretCategorySchema = z.enum(['llm', 'integration', 'custom']);
export type SecretCategory = z.infer<typeof SecretCategorySchema>;

export const IntegrationCategorySchema = z.enum(['models', 'core', 'integration', 'service']);
export type IntegrationCategory = z.infer<typeof IntegrationCategorySchema>;

export const SecretSlotSchema = z.object({
  key: z.string(),
  label: z.string(),
  category: SecretCategorySchema,
  provider: z.string().optional(),
  description: z.string(),
  required: z.boolean(),
  validationHint: z.string().optional(),
  docsUrl: z.string().optional(),
  filePathEnvKey: z.string().optional(),
  /**
   * When false, the admin dialog uses a plaintext field. Omitted means
   * the value is treated as a credential (masked).
   */
  sensitive: z.boolean().optional(),
});
export type SecretSlot = z.infer<typeof SecretSlotSchema>;

export const IntegrationDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  category: IntegrationCategorySchema,
  icon: z.string().optional(),
  secrets: z.array(SecretSlotSchema),
});
export type IntegrationDefinition = z.infer<typeof IntegrationDefinitionSchema>;

/**
 * Where a resolved secret's value actually came from.
 *
 * - `ui` — the encrypted at-rest store (Admin → Service Secrets, or an OAuth callback).
 * - `env` — a real environment variable.
 * - `mount` — a mounted secret file, hydrated into `process.env` at boot. Split
 *   out from `env` so an operator migrating a deployment onto Kubernetes can
 *   see, per secret, whether the mount took over or a leftover env var is still
 *   supplying the value.
 * - `file` — a single credential named by a path env var (`GITHUB_APP_PRIVATE_KEY_PATH`).
 * - `unset` — nothing resolved.
 */
export const SecretSourceSchema = z.enum(['ui', 'env', 'mount', 'file', 'unset']);
export type SecretSource = z.infer<typeof SecretSourceSchema>;

export const SecretStatusItemSchema = z.object({
  key: z.string(),
  label: z.string(),
  category: SecretCategorySchema,
  provider: z.string().optional(),
  description: z.string(),
  required: z.boolean(),
  isSet: z.boolean(),
  source: SecretSourceSchema,
  updatedAt: z.number().optional(),
  // Optional canonical "where to get this key" URL forwarded from the
  // matching {@link SecretSlot}. UI surfaces (the secret-input dialog,
  // potentially a row-level affordance) render this as a clickable
  // link so admins don't have to leave the page to grok where keys
  // come from. Surfaced here — not just in the slot definition — so
  // the wire response carries the link for downstream consumers.
  docsUrl: z.string().optional(),
  /**
   * When false, the admin dialog uses a plaintext field. Omitted means
   * the value is treated as a credential (masked).
   */
  sensitive: z.boolean().optional(),
  /**
   * After this slot is saved on Admin → Service Secrets, navigate here.
   */
  redirectAfterSet: z.string().optional(),
});
export type SecretStatusItem = z.infer<typeof SecretStatusItemSchema>;

export const IntegrationSecretStatusSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: IntegrationCategorySchema,
  icon: z.string().optional(),
  secrets: z.array(SecretStatusItemSchema),
  ready: z.boolean(),
  missingRequired: z.array(z.string()),
});
export type IntegrationSecretStatus = z.infer<typeof IntegrationSecretStatusSchema>;

export const CustomSecretSchema = z.object({
  key: z.string(),
  isSet: z.boolean(),
  updatedAt: z.number().optional(),
});
export type CustomSecret = z.infer<typeof CustomSecretSchema>;

export const SecretsStatusResponseSchema = z.object({
  integrations: z.array(IntegrationSecretStatusSchema),
  customSecrets: z.array(CustomSecretSchema),
});
export type SecretsStatusResponse = z.infer<typeof SecretsStatusResponseSchema>;

export const SetSecretRequestSchema = z.object({
  value: z.string().min(1),
});
export type SetSecretRequest = z.infer<typeof SetSecretRequestSchema>;

/**
 * Response for `PUT /admin/secrets/:name` — the stored secret's identity and
 * timestamps. Never includes the secret value.
 */
export const SecretMutationResponseSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    create_time_ms: z.number(),
    update_time_ms: z.number(),
  })
  .meta({ id: 'SecretMutationResponse' });
export type SecretMutationResponse = z.infer<typeof SecretMutationResponseSchema>;

// --- Sandbox provider (Runloop) key health ---
//
// Surfaces whether an org's *stored* RUNLOOP_API_KEY actually works, as
// opposed to merely being set. The org-setup gate uses this to re-route a
// bootstrapped org with a revoked / missing / trial key back into the
// wizard's API-keys step, where re-entry is validated against Runloop's
// `/v1/accounts/me`.
//
// - `ok`      — key resolves to a usable (non-trial) account.
// - `missing` — no key is configured for the org.
// - `invalid` — key is present but Runloop rejected it (revoked / unverifiable).
// - `trial`   — key authenticates against a trial account (can't run agents).
export const SandboxProviderHealthStatusSchema = z.enum(['ok', 'missing', 'invalid', 'trial']);
export type SandboxProviderHealthStatus = z.infer<typeof SandboxProviderHealthStatusSchema>;

export const SandboxProviderHealthResponseSchema = z.object({
  status: SandboxProviderHealthStatusSchema,
  // Present for `ok` / `trial` — the verified account behind the key, so the
  // UI can confirm which Runloop account (and tier) the org is bound to.
  account: z
    .object({
      id: z.string(),
      name: z.string(),
      tier: z.string(),
      tierLabel: z.string(),
    })
    .optional(),
});
export type SandboxProviderHealthResponse = z.infer<typeof SandboxProviderHealthResponseSchema>;

/**
 * Lightweight secret descriptor returned by `GET /secrets/available`.
 * Contains names only (no values). Used by the env var picker when
 * launching agents or editing personas.
 *
 * Three categories surface here, and they are exactly what an
 * organization creates in order to hand it to an agent:
 * - `custom` — org-tier ad-hoc row written via Admin → Secrets.
 * - `user` — user-tier row owned by the launching user (`/me/secrets`).
 * - `team` — team-tier row from a team the launching user belongs to.
 *
 * Integration credentials never appear: registry slots
 * (`ANTHROPIC_API_KEY`, `RUNLOOP_API_KEY`), plugin-declared keys, and
 * service-tier rows (GitHub App, OAuth client creds) are all credentials
 * Reflex holds to operate the org, and binding one to an env var is how a
 * member would read it out of a devbox they launched. The server enforces
 * the same rule at launch (`SecretService.isAgentBindableSecretName`).
 */
export const AvailableSecretCategorySchema = z.enum(['custom', 'user', 'team']);
export type AvailableSecretCategory = z.infer<typeof AvailableSecretCategorySchema>;

export const AvailableSecretSchema = z
  .object({
    name: z.string(),
    label: z.string(),
    category: AvailableSecretCategorySchema,
    /**
     * Owning group's display name: the team name on `team` rows, a fixed
     * "Your Secrets" on `user` rows, unset on org `custom` rows. The
     * picker groups team rows by this value.
     */
    provider: z.string().optional(),
    /**
     * `sec_*` row id for `user`/`team` entries. Absent for `custom`
     * (org-tier) rows which are still addressed by friendly name.
     * The env-var picker stores this as the `secretRef` value when set so
     * launch-time resolution can probe the user/team managers by id.
     */
    id: z.string().optional(),
  })
  // Tagged so the OpenAPI generator emits one reusable `AvailableSecret`
  // component referenced by the `secrets` routes. See
  // docs/internal/architecture/api-contract.md.
  .meta({ id: 'AvailableSecret' });
export type AvailableSecret = z.infer<typeof AvailableSecretSchema>;

export const AvailableSecretsResponseSchema = z.object({
  secrets: z.array(AvailableSecretSchema),
});
export type AvailableSecretsResponse = z.infer<typeof AvailableSecretsResponseSchema>;

/**
 * Query-string contract for `GET /secrets/available`. Mirrors
 * `ResourceOwnerSchema` shape so callers can pass the picker's
 * `owner` context straight through. Server validates the caller
 * matches the owner before fanning out tier reads.
 *
 * `ownerId` is a single prefixed id whose kind (user / team /
 * organization / project / …) is derived from the id's prefix.
 * Omitted = "available secrets visible to the caller across every
 * tier".
 */
export const AvailableSecretsRequestSchema = z.object({
  name: z.string().optional(),
  ownerId: z.string().optional(),
});
export type AvailableSecretsRequest = z.infer<typeof AvailableSecretsRequestSchema>;

// --- Secret manager / rotation status ---
//
// Surfaced on the Admin → Secrets → Storage detail page. Conveys which
// `SecretManager` implementation is active and (for the encrypted one)
// how much of the `secrets` table is still on a retired key. We
// deliberately expose the 16-hex-char key fingerprints — they are
// SHA-256 truncations of the master keys, not the keys themselves, and
// operators need them to verify which key the manager is rolling to.

export const SecretManagerKindSchema = z.enum(['plaintext', 'encrypted', 'kms', 'unknown']);
export type SecretManagerKind = z.infer<typeof SecretManagerKindSchema>;

export const SecretManagerRotationStatusSchema = z.object({
  /** True when the underlying manager implements `Rotatable`. */
  supported: z.boolean(),
  /**
   * Fingerprint of the primary key (16 lowercase hex chars). Present
   * only for the encrypted manager.
   */
  primaryKeyId: z.string().optional(),
  /** Decrypt-only key fingerprints, in declared order. Encrypted manager only. */
  retiredKeyIds: z.array(z.string()).optional(),
  /** Total number of rows in the `secrets` table. */
  totalSecrets: z.number().int().nonnegative(),
  /** Rows already encrypted under {@link primaryKeyId}. Encrypted manager only. */
  onPrimary: z.number().int().nonnegative().optional(),
  /**
   * Rows that would be re-encrypted on the next `rotate()` pass, i.e.
   * `totalSecrets - onPrimary`. Encrypted manager only.
   */
  needsRotation: z.number().int().nonnegative().optional(),
});
export type SecretManagerRotationStatus = z.infer<typeof SecretManagerRotationStatusSchema>;

export const SecretManagerStatusSchema = z.object({
  /** Stable id surfaced by the manager (e.g. `encrypted-aes-gcm-v1[org]`). */
  id: z.string(),
  kind: SecretManagerKindSchema,
  rotation: SecretManagerRotationStatusSchema,
});
export type SecretManagerStatus = z.infer<typeof SecretManagerStatusSchema>;

export const RotateSecretsRequestSchema = z
  .object({
    batchSize: z.number().int().positive().optional(),
  })
  .strict();
export type RotateSecretsRequest = z.infer<typeof RotateSecretsRequestSchema>;

// --- User-tier credentials (PRD: secret-storage.md, claude-max.md) ---
//
// User-tier credentials are personal credentials registered by individual
// developers, isolated per user, and never visible to org admins.

/**
 * Credential kinds that can be registered on the user tier.
 * Each kind has its own validation schema and mount adapter.
 */
export const UserCredentialKindSchema = z.enum(['claude-max', 'codex-subscription']);
export type UserCredentialKind = z.infer<typeof UserCredentialKindSchema>;

/**
 * Claude Max OAuth blob shape as written by `claude login`.
 * The file lives at `~/.claude/.credentials.json` or
 * `$CLAUDE_CONFIG_DIR/.credentials.json`.
 *
 * SECURITY: Token values must never be logged, echoed to UI, or surfaced
 * in error messages. Only the structural shape is validated; tokens are
 * opaque strings from the validator's perspective.
 */
export const ClaudeOAuthInnerSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  expiresAt: z.number().int().positive(),
  scopes: z.array(z.string()),
  // Preserved so the credential mounted on the devbox keeps the Max
  // subscription markers Claude Code reads from `.credentials.json`.
  // Stripping these (Zod's default for unknown keys) reduces the blob
  // to a plain OAuth token and Claude Code no longer recognises the
  // Max tier — symptom: agent launches succeed but the on-devbox CLI
  // falls back to anonymous / rate-limited behaviour.
  subscriptionType: z.string().optional(),
  rateLimitTier: z.string().optional(),
});
export type ClaudeOAuthInner = z.infer<typeof ClaudeOAuthInnerSchema>;

/**
 * Only `claudeAiOauth` is persisted and mounted. A real `.credentials.json`
 * also carries sibling keys like `mcpOAuth` (per-MCP-server OAuth tokens for
 * servers the user authed locally). Those are intentionally dropped here (Zod
 * strips unknown keys): they're local-machine state (their `redirectUri` is a
 * `localhost` callback), they carry separate bearer tokens with their own
 * expiry that we never refresh, and the devbox gets platform-injected MCP auth
 * anyway — propagating them would leak the user's personal MCP tokens into
 * every devbox. Do not switch this to `.passthrough()` without revisiting that.
 */
export const ClaudeMaxCredentialSchema = z.object({
  claudeAiOauth: ClaudeOAuthInnerSchema,
});
export type ClaudeMaxCredential = z.infer<typeof ClaudeMaxCredentialSchema>;

/**
 * Codex subscription auth blob as written by `codex login`.
 * The CLI owns this file's exact shape, so we validate only that it is a
 * non-array JSON object and mount it back to `~/.codex/auth.json`.
 */
export const CodexSubscriptionCredentialSchema = z
  .record(z.string(), z.unknown())
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Credential JSON cannot be empty',
  });
export type CodexSubscriptionCredential = z.infer<typeof CodexSubscriptionCredentialSchema>;

/**
 * Build the shared "secret alias / model-provider secret name" validator:
 * a non-empty, ≤64-character `[a-zA-Z0-9_-]+` string that never contains
 * the reserved `__` separator. The only caller-configurable delta is the
 * `label` word interpolated into each validation error message.
 *
 * Two exported schemas are built from this factory —
 * {@link CredentialAliasSchema} (user-tier credential aliases) and
 * `ModelProviderSecretNameSchema` (org/team/user model-provider key names
 * in `model-provider-secrets.types.ts`) — so a name valid on one surface
 * is valid on the other. Centralising the chain pins the load-bearing
 * `!s.includes('__')` invariant in one place: `__` is the secret store's
 * reserved join separator, and the two copies previously had nothing
 * keeping them in lockstep (the `ModelProviderSecretNameSchema` docblock
 * only *said* it "mirrors" `CredentialAliasSchema`). A future rules
 * change now lands once. Mirrors the `paginationParamsSchema(bounds)`
 * factory pattern in `pagination.types.ts`.
 */
export function secretAliasNameSchema(options: { label: string }) {
  const { label } = options;
  return z
    .string()
    .min(1, `${label} cannot be empty`)
    .max(64, `${label} must be 64 characters or fewer`)
    .regex(
      /^[a-zA-Z0-9_-]+$/,
      `${label} must contain only letters, numbers, underscores, and dashes`,
    )
    .refine((s) => !s.includes('__'), {
      message: `${label} cannot contain "__" (reserved separator)`,
    });
}

/**
 * Alias format used for user credentials and other multi-credential slots.
 * Must be safe for use in secret names (no `__` separator collision).
 * Built by {@link secretAliasNameSchema} so it stays structurally identical
 * to `ModelProviderSecretNameSchema`; the two share one source of truth for
 * the `__`-free invariant and the `[a-zA-Z0-9_-]+` alphabet.
 */
export const CredentialAliasSchema = secretAliasNameSchema({ label: 'Alias' });
export type CredentialAlias = z.infer<typeof CredentialAliasSchema>;

/**
 * User credential as stored and returned by GET /me/credentials.
 * The `value` (encrypted ciphertext) is never exposed — only metadata.
 *
 * `createdAt` and `updatedAt` are optional because the current SecretManager
 * implementations don't track timestamps. Future implementations may provide
 * these values; the frontend should handle their absence gracefully.
 */
export const UserCredentialSchema = z.object({
  kind: UserCredentialKindSchema,
  alias: CredentialAliasSchema,
  createdAt: z.number().int().nonnegative().optional(),
  updatedAt: z.number().int().nonnegative().optional(),
});
export type UserCredential = z.infer<typeof UserCredentialSchema>;

export const UserCredentialsListResponseSchema = z.object({
  credentials: z.array(UserCredentialSchema),
});
export type UserCredentialsListResponse = z.infer<typeof UserCredentialsListResponseSchema>;

/**
 * Request body for POST /me/credentials/claude-max.
 * The client pastes the contents of ~/.claude/.credentials.json.
 */
export const RegisterClaudeMaxRequestSchema = z.object({
  blob: ClaudeMaxCredentialSchema,
  alias: CredentialAliasSchema.optional(),
});
export type RegisterClaudeMaxRequest = z.infer<typeof RegisterClaudeMaxRequestSchema>;

/**
 * Request body for POST /me/credentials/codex-subscription.
 * The client pastes the contents of ~/.codex/auth.json.
 */
export const RegisterCodexSubscriptionRequestSchema = z.object({
  blob: CodexSubscriptionCredentialSchema,
  alias: CredentialAliasSchema.optional(),
});
export type RegisterCodexSubscriptionRequest = z.infer<
  typeof RegisterCodexSubscriptionRequestSchema
>;

/**
 * Response body for the subscription registration routes
 * (`POST /me/credentials/claude-max`, `POST /me/credentials/codex-subscription`).
 *
 * `modelProviderSecretId` is the `mps_*` mirror row the key pickers select by,
 * returned so a client can act on the subscription it just registered — select
 * it in a launch form, or re-point a running agent at it — without guessing
 * which row appeared. Mirroring is best-effort on the server, so the field is
 * nullable: a null means "registered, but you'll have to pick it from the list".
 */
export const RegisterSubscriptionResponseSchema = z.object({
  success: z.boolean(),
  modelProviderSecretId: z.string().nullable(),
});
export type RegisterSubscriptionResponse = z.infer<typeof RegisterSubscriptionResponseSchema>;

/**
 * Response body for `GET /me/credentials/:kind/status`. A dead Codex
 * subscription surfaces as a `codex_subscription_needs_reconnect` error at
 * launch ("learn at launch"), the same way GitHub/Google tokens surface on
 * use, so this reports only whether a credential is configured.
 */
export const CredentialStatusResponseSchema = z.object({
  configured: z.boolean(),
  kind: UserCredentialKindSchema,
  alias: z.string(),
});
export type CredentialStatusResponse = z.infer<typeof CredentialStatusResponseSchema>;

// --- Auth method for agent launch ---
//
// Determines how the agent authenticates to its model provider.

export const AgentAuthMethodSchema = z.enum(['org-key', 'claude-max', 'codex-subscription']);
export type AgentAuthMethod = z.infer<typeof AgentAuthMethodSchema>;

/**
 * The org-key environment variables a personal subscription supersedes at
 * launch — the provider API key its mounted CLI auth file replaces, plus that
 * provider's custom base-URL var.
 *
 * A subscription launch must neither *require* these nor *receive* them:
 *   - requiring them rejects a launch for a key it was never going to use
 *     (an org that has no `ANTHROPIC_API_KEY` is exactly who Claude Max is
 *     for), and
 *   - receiving them would authenticate — or, via the base URL, route — the
 *     user's personal OAuth token through the org's billing account.
 *
 * Scoped deliberately to the subscription's *own* provider. An endpoint keyed
 * on some other provider (e.g. the Vercel AI Gateway's `AI_GATEWAY_API_KEY`)
 * is not superseded by a Claude/Codex subscription, so it still resolves
 * normally rather than being silently dropped.
 *
 * Centralized here because the launch preflight and the agent provider both
 * read it and must agree: if only one treats a method as subscription-backed,
 * the launch fails at whichever layer still demands the org key.
 */
const SUBSCRIPTION_SUPERSEDED_ENV_VARS: Record<AgentAuthMethod, readonly string[]> = {
  'org-key': [],
  // Keep in lockstep with the `anthropic` / `openai` entries in the server's
  // `PROVIDER_ENV_VAR_NAMES` and in `MODEL_PROVIDER_BASE_URL_ENV_VARS` — a
  // server-side test asserts the three agree.
  'claude-max': ['ANTHROPIC_API_KEY', 'ANTHROPIC_BASE_URL'],
  'codex-subscription': ['OPENAI_API_KEY', 'OPENAI_BASE_URL'],
};

/** See {@link SUBSCRIPTION_SUPERSEDED_ENV_VARS}. Empty for `org-key`. */
export function subscriptionSupersededEnvVars(authMethod: AgentAuthMethod): readonly string[] {
  return SUBSCRIPTION_SUPERSEDED_ENV_VARS[authMethod];
}

/**
 * True when the launch authenticates with the user's own subscription
 * credential (Claude Max / Codex) rather than the organization's provider API
 * key. Derived from {@link SUBSCRIPTION_SUPERSEDED_ENV_VARS} so a new auth
 * method cannot be added without deciding which org keys it replaces — the
 * `Record<AgentAuthMethod, ...>` makes omitting one a compile error.
 */
export function usesSubscriptionCredential(authMethod: AgentAuthMethod): boolean {
  return subscriptionSupersededEnvVars(authMethod).length > 0;
}

// --- User and team scoped secrets ---
//
// Both surfaces follow the same "ID-first" model: every row is
// addressed by its `sec_*` id, while `name` is a human-readable
// display label scoped per-owner. The plaintext `value` is never
// returned by list/get endpoints — clients store the id and trust
// the server-side `owner_id` filter for tenancy.

/**
 * Display-name format for user and team secrets. Same shape as a
 * conventional environment variable to keep the devbox injection
 * story simple (the display name doubles as the env var key when
 * the secret is mounted into an agent's environment).
 */
export const ScopedSecretNameSchema = z
  .string()
  .min(1, 'Name cannot be empty')
  .max(64, 'Name must be 64 characters or fewer')
  .regex(
    /^[A-Z][A-Z0-9_]*$/,
    'Name must start with an uppercase letter and contain only uppercase letters, digits, and underscores',
  );
export type ScopedSecretName = z.infer<typeof ScopedSecretNameSchema>;

export const ScopedSecretSchema = z
  .object({
    id: coreIds.schema('secret'),
    name: ScopedSecretNameSchema,
    ownerId: z.string(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
  })
  .meta({ id: 'ScopedSecret' });
export type ScopedSecret = z.infer<typeof ScopedSecretSchema>;

export const ScopedSecretsListResponseSchema = z.object({
  secrets: z.array(ScopedSecretSchema),
});
export type ScopedSecretsListResponse = z.infer<typeof ScopedSecretsListResponseSchema>;

export const CreateScopedSecretRequestSchema = z.object({
  name: ScopedSecretNameSchema,
  value: z.string().min(1, 'Value cannot be empty'),
});
export type CreateScopedSecretRequest = z.infer<typeof CreateScopedSecretRequestSchema>;

export const UpdateScopedSecretRequestSchema = z
  .object({
    name: ScopedSecretNameSchema.optional(),
    value: z.string().min(1).optional(),
  })
  .refine((v) => v.name !== undefined || v.value !== undefined, {
    message: 'At least one of `name` or `value` must be provided',
  });
export type UpdateScopedSecretRequest = z.infer<typeof UpdateScopedSecretRequestSchema>;
